/**
 * CDD (Customer Due Diligence, M32210) 문서별 변수 정의 + 영문 라벨/타입 규칙.
 *
 * 템플릿(TMP004 Legal Person / TMP005 Legal Arrangement / TMP006 Individual)의
 * [[변수]] 와 1:1 일치. 체크박스는 if-wrap 불리언 (bt_* / at_*).
 */

export type CddDocType = 'individual' | 'legal_person' | 'legal_arrangement';
export type CddFieldType = 'text' | 'date' | 'entity' | 'person' | 'combo' | 'address' | 'clause' | 'msic';

export interface CddDocMeta {
  docType: CddDocType;
  templateId: string;      // 실제 업로드 TMP_ID (재업로드로 바뀌면 조정)
  label: string;
  keys: string[];          // 채우기 대상 변수 (progress/gather 기준)
  checkboxGroup?: { field: string; options: { value: string; label: string }[] } | null;
}

// 공통(3폼 동일): 대리인 + 선언
const PT_KEYS = [
  'pt_full_name', 'pt_ic_passport_no', 'pt_dob', 'pt_residential_address',
  'pt_town', 'pt_state', 'pt_postcode', 'pt_country', 'pt_nationality',
  'pt_occupation', 'pt_employer_name', 'pt_contact_no',
];
const DECL_KEYS = ['signer_name', 'signer_designation', 'declaration_date'];
const COMMON_TAIL = [...PT_KEYS, ...DECL_KEYS];

const IND_KEYS = [
  'ind_full_name', 'ind_ic_passport_no', 'ind_dob', 'ind_residential_address',
  'ind_town', 'ind_state', 'ind_postcode', 'ind_country',
  'ind_mailing_address', 'ind_mailing_town', 'ind_mailing_state', 'ind_mailing_postcode', 'ind_mailing_country',
  'ind_nationality', 'ind_occupation_type', 'ind_employer_name', 'ind_contact_no', 'ind_purpose_of_txn',
];
// Shareholder / BO 테이블 최대 행 수 (UI·데이터 동적 행 상한).
// 문서(TMP004) 반복부: [[if shareholders]]…[[/if]] / [[if bo]]…[[/if]] — mapper 가 배열로 확장.
export const CDD_SHAREHOLDER_MAX = 10;
function shareholderRowKeys(max: number): string[] {
  const out: string[] = [];
  for (let n = 1; n <= max; n++) out.push(`shareholder_name_${n}`, `share_type_${n}`, `share_pct_${n}`);
  return out;
}
function boRowKeys(max: number): string[] {
  const out: string[] = [];
  for (let n = 1; n <= max; n++) out.push(`bo_name_${n}`, `bo_relationship_${n}`);
  return out;
}
const LP_KEYS = [
  'lp_company_name', 'lp_registration_no', 'lp_other_type',
  'lp_country_of_incorporation', 'lp_registered_address', 'lp_town', 'lp_state', 'lp_postcode', 'lp_country',
  'lp_business_address', 'lp_biz_town', 'lp_biz_state', 'lp_biz_postcode', 'lp_biz_country',
  'lp_principle_business', 'lp_contact_no', 'lp_purpose_of_txn', 'lp_directors_partners',
  ...shareholderRowKeys(CDD_SHAREHOLDER_MAX), ...boRowKeys(CDD_SHAREHOLDER_MAX), 'lp_senior_management',
];
const LA_KEYS = [
  'la_name', 'la_registration_no', 'la_other_type',
  'la_country_of_registration', 'la_registered_address', 'la_town', 'la_state', 'la_postcode', 'la_country',
  'la_activity_address', 'la_activity_town', 'la_activity_state', 'la_activity_postcode', 'la_activity_country',
  'la_principle_activity', 'la_contact_no', 'la_purpose_of_txn', 'la_directors_partners',
  'la_settlor_name', 'la_settlor_id', 'la_settlor_address',
  'la_trustee_name', 'la_trustee_id', 'la_trustee_address',
  'la_protector_name', 'la_protector_id', 'la_protector_address',
  'la_beneficiary_name', 'la_beneficiary_id', 'la_beneficiary_address',
  'la_other_bo_name', 'la_other_bo_id', 'la_other_bo_address', 'la_relationship',
];

export const CDD_DOCS: Record<CddDocType, CddDocMeta> = {
  individual: {
    docType: 'individual', templateId: 'TMP006', label: 'Individual',
    keys: [...IND_KEYS, ...COMMON_TAIL], checkboxGroup: null,
  },
  legal_person: {
    docType: 'legal_person', templateId: 'TMP004', label: 'Legal Person',
    keys: [...LP_KEYS, ...COMMON_TAIL],
    checkboxGroup: {
      field: 'lp_business_type',
      options: [
        { value: 'bt_sole', label: 'Sole Proprietorship' }, { value: 'bt_part', label: 'Partnership' },
        { value: 'bt_llp', label: 'Limited Liability Partnership' }, { value: 'bt_pub', label: 'Public Company' },
        { value: 'bt_pvt', label: 'Private Limited Company' }, { value: 'bt_trust', label: 'Trust' },
        { value: 'bt_club', label: 'Club/Society/Charity' }, { value: 'bt_other', label: 'Other' },
      ],
    },
  },
  legal_arrangement: {
    docType: 'legal_arrangement', templateId: 'TMP005', label: 'Legal Arrangement',
    keys: [...LA_KEYS, ...COMMON_TAIL],
    checkboxGroup: {
      field: 'la_type',
      options: [
        { value: 'at_trust', label: 'Trust' }, { value: 'at_club', label: 'Club/Society/Charity' },
        { value: 'at_other', label: 'Others' },
      ],
    },
  },
};

// 날짜는 필수 아님(Skip 가능). 2번째 이후 행(_2..)도 선택.
export function isOptional(key: string): boolean {
  if (/(_date|_dob)$/.test(key) || /mailing/.test(key)) return true;
  const m = key.match(/_(\d+)$/);
  return !!(m && Number(m[1]) >= 2);
}

const OVERRIDES: Record<string, string> = {
  cdd_date: 'Date', full_name: 'Full Name', ic_passport_no: 'NRIC / Passport No.', dob: 'Date of Birth',
  residential_address: 'Residential Address', mailing_address: 'Mailing Address', company_name: 'Company / Business Name',
  registration_no: 'Registration No.', country_of_incorporation: 'Country of Incorporation', country_of_registration: 'Country of Registration',
  registered_address: 'Registered Office Address', business_address: 'Principal Place of Business', activity_address: 'Principal Place of Activity',
  principle_business: 'Principal Business', principle_activity: 'Principal Activity', purpose_of_txn: 'Purpose of Transaction',
  directors_partners: 'Directors / Partners', senior_management: 'Senior Management', other_type: 'Other (specify)',
  occupation_type: 'Occupation Type', employer_name: 'Employer / Nature of Business', contact_no: 'Contact No.',
  signer_name: 'Name', signer_designation: 'Designation', declaration_date: 'Date', relationship: 'Relationship with Trust',
  other_bo_name: 'Other BO Name', other_bo_id: 'Other BO ID', other_bo_address: 'Other BO Address',
};
const TOKENS: Record<string, string> = { no: 'No.', id: 'ID', txn: 'Transaction', pct: 'Percentage', bo: 'BO', ic: 'NRIC', biz: 'Business' };

function stripPrefix(key: string): string { return key.replace(/^(ind|lp|la|pt)_/, ''); }

export function labelize(key: string): string {
  const bare = stripPrefix(key);
  if (OVERRIDES[bare]) return OVERRIDES[bare];
  return bare.split('_').map((w) => TOKENS[w] || (w.charAt(0).toUpperCase() + w.slice(1))).join(' ');
}

export function typeOf(key: string): CddFieldType {
  const b = stripPrefix(key);
  if (/(^|_)(dob|date)$/.test(b)) return 'date';
  if (key === 'lp_company_name' || key === 'la_name') return 'entity';
  if (/share_type_\d+$/.test(key)) return 'combo'; // Ordinary / Preference / Others
  if (/nationality$/.test(key) || /(^|_)occupation(_type)?$/.test(key)) return 'combo';
  if (/_country$/.test(key) || /country_of_(incorporation|registration)$/.test(key)) return 'combo';
  if (/_address$/.test(key)) return 'address';
  if (/full_name$|settlor_name$|trustee_name$|protector_name$|beneficiary_name$|other_bo_name$|shareholder_name_\d+$|bo_name_\d+$|directors_partners$|senior_management$|signer_name$/.test(key)) return 'person';
  return 'text';
}

// ─── 주소 그룹: 앵커(*_address) → 형제 컴포넌트 필드 ────────────────
// 모달에서 구조화 입력 시 town/state/postcode/country 를 함께 채운다.
// 컴포넌트가 없는 단일 라인 주소(settlor/trustee 등)는 line 만 존재.
export interface CddAddressGroup {
  line: string;       // 앵커 *_address 변수
  town?: string;
  state?: string;
  postcode?: string;
  country?: string;
}

export const CDD_ADDRESS_GROUPS: CddAddressGroup[] = [
  // Individual
  { line: 'ind_residential_address', town: 'ind_town', state: 'ind_state', postcode: 'ind_postcode', country: 'ind_country' },
  { line: 'ind_mailing_address', town: 'ind_mailing_town', state: 'ind_mailing_state', postcode: 'ind_mailing_postcode', country: 'ind_mailing_country' },
  // 공통 대리인(PT)
  { line: 'pt_residential_address', town: 'pt_town', state: 'pt_state', postcode: 'pt_postcode', country: 'pt_country' },
  // Legal Person
  { line: 'lp_registered_address', town: 'lp_town', state: 'lp_state', postcode: 'lp_postcode', country: 'lp_country' },
  { line: 'lp_business_address', town: 'lp_biz_town', state: 'lp_biz_state', postcode: 'lp_biz_postcode', country: 'lp_biz_country' },
  // Legal Arrangement
  { line: 'la_registered_address', town: 'la_town', state: 'la_state', postcode: 'la_postcode', country: 'la_country' },
  { line: 'la_activity_address', town: 'la_activity_town', state: 'la_activity_state', postcode: 'la_activity_postcode', country: 'la_activity_country' },
  // 단일 라인 (컴포넌트 없음)
  { line: 'la_settlor_address' }, { line: 'la_trustee_address' }, { line: 'la_protector_address' },
  { line: 'la_beneficiary_address' }, { line: 'la_other_bo_address' },
];

export function addressGroupOf(key: string): CddAddressGroup | null {
  return CDD_ADDRESS_GROUPS.find((g) => g.line === key) || null;
}

export type CddComboSource =
  | 'nationality'
  | 'occupation'
  | 'country'
  | 'shareClass'
  | 'companyType'
  | 'purpose'
  | 'companySubType'
  | 'race'
  | 'idType';

/** combo 필드의 옵션 소스. shareClass = Ordinary/Preference/Others 또는 class_of_share. combo 아니면 null. */
export function comboSourceOf(key: string): CddComboSource | null {
  if (/share_type_\d+$/.test(key)) return 'shareClass';
  if (/nationality$/.test(key)) return 'nationality';
  if (/(^|_)occupation(_type)?$/.test(key)) return 'occupation';
  if (/_country$/.test(key) || /country_of_(incorporation|registration)$/.test(key)) return 'country';
  if (/class_of_share$/.test(key) || /share_type(_\d+)?$/.test(key)) return 'shareClass';
  if (key === 'company_type' || /company_type$/.test(key)) return 'companyType';
  if (key === 'purpose') return 'purpose';
  if (key === 'company_sub_type' || /company_sub_type$/.test(key)) return 'companySubType';
  if (/race$/.test(key)) return 'race';
  if (/id_type$/.test(key)) return 'idType';
  return null;
}
